/**
 * Ndev CES Subscribe Component
 * 处理订阅表单的交互逻辑
 */

class NdevCesSubscribeForm extends HTMLElement {
  constructor() {
    super();
    this.emailInput = null;
    this.checkbox = null;
    this.submitButton = null;
    this.errorMessage = null;
    this.form = null;
    this.section = null;
    this.klaviyoListId = null;
    this.successMessage = '';
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.form = this.querySelector('.subscribe-form-inner');
    this.emailInput = this.querySelector('.email-input');
    this.checkbox = this.querySelector('.agree-checkbox');
    this.submitButton = this.querySelector('.subscribe-button');
    this.errorMessage = this.querySelector('.error-message');
    this.section = this.closest('.ndev-ces-subscribe');
    
    // 获取配置
    this.klaviyoPublicKey = this.dataset.klaviyoPublicKey || '';
    this.klaviyoListId = this.dataset.klaviyoListId || '';
    this.successMessage = this.dataset.successMessage || '🎉 Thanks for subscription! 🎉';

    if (!this.form || !this.emailInput || !this.checkbox || !this.submitButton) {
      console.error('NdevCesSubscribeForm: Required elements not found');
      return;
    }

    // 保存按钮原始文本
    const buttonSpan = this.submitButton.querySelector('span');
    if (buttonSpan && !this.submitButton.dataset.originalText) {
      this.submitButton.dataset.originalText = buttonSpan.textContent.trim();
    }

    this.bindEvents();
    this.updateButtonState();
  }

  bindEvents() {
    // 邮箱输入事件
    this.emailInput.addEventListener('input', () => {
      this.handleEmailInput();
    });

    this.emailInput.addEventListener('focus', () => {
      this.clearError();
    });

    // 复选框变化事件
    this.checkbox.addEventListener('change', () => {
      this.updateButtonState();
    });

    // 表单提交事件
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();

      window.dataLayer.push({
        event:"ga4Event",
        event_name:"subscribe",
        event_parameters:{
            button_name: this.submitButton.innerText,
            module_name: "Ndev Ces Subscribe"
        }
    });
      this.handleSubmit();
    });
  }

  handleEmailInput() {
    this.clearError();
    // 按钮状态只由复选框控制，不需要在这里更新
  }

  updateButtonState() {
    const isChecked = this.checkbox.checked;

    // 只要复选框已勾选，就启用按钮（不需要等待输入邮箱）
    if (isChecked) {
      this.submitButton.disabled = false;
    } else {
      this.submitButton.disabled = true;
    }
  }

  isValidEmail(email) {
    if (!email) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  clearError() {
    if (this.errorMessage) {
      this.errorMessage.classList.remove('show');
      this.errorMessage.textContent = '';
    }
  }

  showError(message) {
    if (this.errorMessage) {
      this.errorMessage.textContent = message;
      this.errorMessage.classList.add('show');
    }
  }

  async handleSubmit() {
    const email = this.emailInput.value.trim();
    
    // 验证邮箱格式
    if (!this.isValidEmail(email)) {
      this.showError('This email is invalid');
      return;
    }

    // 验证复选框
    if (!this.checkbox.checked) {
      this.showError('Please agree to the Terms of Use and Privacy Policy');
      return;
    }

    // 清除错误信息
    this.clearError();

    // 发送订阅请求（不等待响应，直接恢复按钮状态）
    try {
      // 如果有 Klaviyo Public Key 和 List ID，使用 Klaviyo API
      if (this.klaviyoPublicKey && this.klaviyoListId) {
        // 不等待响应，直接处理成功
        this.subscribeToKlaviyo(email).catch(error => {
          console.error('Subscribe error:', error);
          // 静默处理错误，不显示给用户
        });
      } else {
        // 否则使用 Shopify 的邮件订阅
        this.subscribeToShopify(email).catch(error => {
          console.error('Subscribe error:', error);
          // 静默处理错误，不显示给用户
        });
      }

      // 直接处理成功（不等待响应）
      this.handleSuccess();
    } catch (error) {
      console.error('Subscribe error:', error);
      // 即使出错也显示成功（因为请求已发送）
      this.handleSuccess();
    }
  }

  async subscribeToKlaviyo(email) {
    // Klaviyo API 订阅逻辑
    // 参考: https://a.klaviyo.com/client/subscriptions
    const revision = "2025-10-15"; // Fixed revision date for Klaviyo API

    // Build URL with company_id parameter as query string (public API key / site ID)
    const url = new URL("https://a.klaviyo.com/client/subscriptions");
    url.searchParams.append("company_id", this.klaviyoPublicKey);

    // Build request body according to Klaviyo API documentation
    const requestBody = {
      data: {
        type: "subscription",
        attributes: {
          profile: {
            data: {
              type: "profile",
              attributes: {
                email: email,
                subscriptions: {
                  email: {
                    marketing: {
                      consent: "SUBSCRIBED",
                    },
                  },
                },
              },
            },
          },
        },
      },
    };

    // Add list relationship if listId is provided and not empty
    if (this.klaviyoListId && this.klaviyoListId.trim() !== "") {
      requestBody.data.relationships = {
        list: {
          data: {
            type: "list",
            id: this.klaviyoListId,
          },
        },
      };
    }

    const response = await fetch(url.toString(), {
      method: "POST",
      headers: {
        "Content-Type": "application/vnd.api+json",
        revision: revision,
      },
      body: JSON.stringify(requestBody),
    });

    // 获取响应文本，检查是否为空
    const responseText = await response.text();

    if (!response.ok) {
      // 尝试解析错误响应
      let errorData = {};
      try {
        if (responseText) {
          errorData = JSON.parse(responseText);
        }
      } catch (e) {
        // 如果无法解析 JSON，使用原始文本
        errorData = { message: responseText || `HTTP ${response.status}: ${response.statusText}` };
      }
      
      throw new Error(errorData.message || errorData.detail || errorData.errors?.[0]?.detail || 'Subscription failed');
    }

    // 如果响应为空，返回空对象
    if (!responseText || responseText.trim() === '') {
      return {};
    }

    // 尝试解析 JSON 响应
    try {
      return JSON.parse(responseText);
    } catch (e) {
      // 如果无法解析，返回空对象（成功响应可能没有 body）
      return {};
    }
  }

  async subscribeToShopify(email) {
    // Shopify 邮件订阅逻辑
    const formData = new FormData();
    formData.append('contact[email]', email);
    formData.append('form_type', 'customer');

    const response = await fetch('/contact', {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Subscription failed');
    }

    return response;
  }

  handleSuccess() {
    // 更新标题为成功消息
    const titleElement = this.section?.querySelector('.subscribe-title h2');
    if (titleElement) {
      titleElement.textContent = this.successMessage;
    }

    // 添加成功状态类
    if (this.section) {
      this.section.classList.add('subscribe-success');
    }

    // 恢复按钮文本为原始文本
    const originalText = this.submitButton.dataset.originalText;
    if (originalText && this.submitButton.querySelector('span')) {
      this.submitButton.querySelector('span').textContent = originalText;
    }

    // 禁用表单和按钮
    this.emailInput.disabled = true;
    this.checkbox.disabled = true;
    this.submitButton.disabled = true;
  }

  handleError(error) {
    // 恢复按钮状态
    this.submitButton.disabled = false;
    this.submitButton.querySelector('span').textContent = 'Subscribe';

    // 显示错误信息
    let errorMessage = 'Subscription failed. Please try again.';
    
    if (error.message) {
      if (error.message.includes('already') || error.message.includes('exists')) {
        errorMessage = 'This email is already registered';
      } else if (error.message.includes('invalid')) {
        errorMessage = 'This email is invalid';
      } else {
        errorMessage = error.message;
      }
    }

    this.showError(errorMessage);
  }
}

// 注册自定义元素
if (!customElements.get('ndev-ces-subscribe-form')) {
  customElements.define('ndev-ces-subscribe-form', NdevCesSubscribeForm);
}

// 降级处理：如果浏览器不支持 Custom Elements
if (typeof window !== 'undefined' && !window.customElements) {
  document.addEventListener('DOMContentLoaded', () => {
    const forms = document.querySelectorAll('ndev-ces-subscribe-form');
    forms.forEach((form) => {
      const instance = new NdevCesSubscribeForm();
      Object.assign(instance, form);
      instance.init();
    });
  });
}